import { createSlice } from '@reduxjs/toolkit';

import {
    updateDoc,
    createNewDocProperties,
    patchDocProperty,
    loadDoc,
} from './document.middleware';
import { logout } from '../../auth/auth-middleware';
import { AppDocument } from 'api';

type DocType = {
    msg: string | null;
    isLoading: boolean;
    error: string | null;
};

type DocumentState = {
    entity: AppDocument | null;
    newCreated: DocType;
    updated: DocType;
    patchedDoc: DocType;
    error: string | null;
    isLoading: boolean;
};

const initialState: DocumentState = {
    entity: null,
    newCreated: { msg: null, isLoading: false, error: null },
    updated: { msg: null, isLoading: false, error: null },
    patchedDoc: { msg: null, isLoading: false, error: null },
    error: null,
    isLoading: false,
};

export const documentSlice = createSlice({
    name: 'app/document',
    initialState,
    reducers: {
        reset: (state) => {
            state.entity = null;
            state.newCreated = { msg: null, isLoading: false, error: null };
            state.updated = { msg: null, isLoading: false, error: null };
            state.patchedDoc = { msg: null, isLoading: false, error: null };
            state.error = null;
            state.isLoading = false;
        },
        setDoc: (state, action: { payload: AppDocument | null }) => {
            state.entity = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            // GET
            .addCase(loadDoc.fulfilled, (state, action) => {
                state.entity = action.payload.data;
                state.error = null;
                state.isLoading = false;
            })
            .addCase(loadDoc.pending, (state) => {
                state.error = null;
                state.isLoading = true;
            })
            .addCase(loadDoc.rejected, (state, action) => {
                const errorMsg = (action.payload as { msg: string }).msg
                    ? (action.payload as { msg: string }).msg
                    : 'Something went wrong';
                state.entity = null;
                state.error = errorMsg;
                state.isLoading = false;
            })
            // PUT
            .addCase(updateDoc.fulfilled, (state, action) => {
                state.updated = {
                    msg: action.payload.data.msg,
                    isLoading: false,
                    error: null,
                };
            })
            .addCase(updateDoc.pending, (state) => {
                state.updated = {
                    msg: null,
                    isLoading: true,
                    error: null,
                };
            })
            .addCase(updateDoc.rejected, (state, action) => {
                state.updated = {
                    msg: null,
                    isLoading: false,
                    error: action.error.message as string,
                };
            })
            // POST
            .addCase(createNewDocProperties.fulfilled, (state, action) => {
                state.newCreated = {
                    msg: action.payload.data.msg,
                    isLoading: false,
                    error: null,
                };
            })
            .addCase(createNewDocProperties.pending, (state) => {
                state.newCreated = {
                    msg: '',
                    isLoading: true,
                    error: null,
                };
            })
            .addCase(createNewDocProperties.rejected, (state, action) => {
                state.newCreated = {
                    msg: '',
                    isLoading: true,
                    error: action.error.message as string,
                };
            })
            // PATCH
            .addCase(patchDocProperty.fulfilled, (state, action) => {
                state.patchedDoc = {
                    msg: action.payload.data.msg,
                    isLoading: false,
                    error: null,
                };
            })
            .addCase(patchDocProperty.pending, (state) => {
                state.patchedDoc = {
                    msg: null,
                    isLoading: true,
                    error: null,
                };
            })
            .addCase(patchDocProperty.rejected, (state, action) => {
                const errorMsg = (action.payload as { msg: string }).msg
                    ? (action.payload as { msg: string }).msg
                    : 'Something went wrong';
                state.patchedDoc = {
                    msg: null,
                    isLoading: false,
                    error: errorMsg,
                };
            })
            .addCase(logout.fulfilled, (state) => {
                state.entity = null;
                state.newCreated = { msg: null, isLoading: false, error: null };
                state.updated = { msg: null, isLoading: false, error: null };
                state.patchedDoc = { msg: null, isLoading: false, error: null };
                state.error = null;
                state.isLoading = false;
            });
    },
});

export const { reset, setDoc } = documentSlice.actions;
export default documentSlice.reducer;
